// @ts-nocheck
/**
 * MobileFullScreenSticky Component
 *
 * Full-screen sticky note view for mobile devices.
 * Shows one sticky at a time with navigation arrows.
 *
 * Features:
 * - Full viewport height (minus safe areas)
 * - Editable title and content
 * - Left/right navigation arrows when multiple notes exist
 * - Pagination indicator (1 of 3)
 * - Minimize and delete in header
 */

import React, { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Minus, X, Pencil, StickyNote, ChevronLeft, ChevronRight, Copy, Check } from 'lucide-react';
import { logger } from '@shared/utils/logger';
import type { UserStickyNote } from '@shared/types/userStickies';
import { stickyColorSchemes, dynamicsColorScheme, DEFAULT_STICKY_PLACEHOLDER } from '@shared/types/userStickies';
import { useUIStore } from '@shared/store/uiStore';

// Debounce time to prevent prop sync from overwriting user typing
const TYPING_DEBOUNCE_MS = 1000;

interface MobileFullScreenStickyProps {
  sticky: UserStickyNote;
  currentIndex: number;
  totalCount: number;
  onContentChange: (content: string) => void;
  onTitleChange: (title: string) => void;
  onMinimize: () => void;
  onDelete: () => void;
  onNext: () => void;
  onPrev: () => void;
}

/**
 * Animation variants for full-screen panel
 */
const panelVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 30 },
  },
  exit: { opacity: 0, y: 20, transition: { duration: 0.2 } },
};

const reducedMotionVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.1 } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
};

export const MobileFullScreenSticky: React.FC<MobileFullScreenStickyProps> = ({
  sticky,
  currentIndex,
  totalCount,
  onContentChange,
  onTitleChange,
  onMinimize,
  onDelete,
  onNext,
  onPrev,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const themeClass = useUIStore((state) => state.themeClass);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const [localContent, setLocalContent] = useState(sticky.content);
  const [localTitle, setLocalTitle] = useState(sticky.title);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);

  // Track when user is actively typing
  const lastContentChangeRef = useRef<number>(0);
  const lastTitleChangeRef = useRef<number>(0);

  // Color scheme
  const isDynamicsTheme = themeClass === 'theme-dynamics';
  const colorScheme = useMemo(() => {
    return isDynamicsTheme ? dynamicsColorScheme : stickyColorSchemes[sticky.color];
  }, [isDynamicsTheme, sticky.color]);

  // Sync local content with prop (only if not typing)
  useEffect(() => {
    const timeSinceLastChange = Date.now() - lastContentChangeRef.current;
    if (timeSinceLastChange > TYPING_DEBOUNCE_MS) {
      setLocalContent(sticky.content);
    }
  }, [sticky.content]);

  // Sync local title with prop
  useEffect(() => {
    const timeSinceLastChange = Date.now() - lastTitleChangeRef.current;
    if (timeSinceLastChange > TYPING_DEBOUNCE_MS) {
      setLocalTitle(sticky.title);
    }
  }, [sticky.title]);

  // Focus title input when editing
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Handle content change
  const handleContentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newContent = e.target.value;
      lastContentChangeRef.current = Date.now();
      setLocalContent(newContent);
      onContentChange(newContent);
    },
    [onContentChange]
  );

  // Handle title change
  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    lastTitleChangeRef.current = Date.now();
    setLocalTitle(newTitle);
  }, []);

  // Handle title blur - save
  const handleTitleBlur = useCallback(() => {
    setIsEditingTitle(false);
    if (localTitle.trim() !== sticky.title) {
      onTitleChange(localTitle.trim() || 'New Note');
    }
  }, [localTitle, sticky.title, onTitleChange]);

  // Handle title keydown
  const handleTitleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleTitleBlur();
      } else if (e.key === 'Escape') {
        setLocalTitle(sticky.title);
        setIsEditingTitle(false);
      }
    },
    [handleTitleBlur, sticky.title]
  );

  // Handle copy to clipboard
  const handleCopy = useCallback(async () => {
    try {
      const textToCopy = localContent || '';
      await navigator.clipboard.writeText(textToCopy);
      setHasCopied(true);
      setTimeout(() => setHasCopied(false), 2000);
    } catch (err) {
      logger.error('[MobileFullScreenSticky] Failed to copy to clipboard:', err);
    }
  }, [localContent]);

  const variants = prefersReducedMotion ? reducedMotionVariants : panelVariants;
  const hasMultiple = totalCount > 1;
  const canGoNext = currentIndex < totalCount - 1;
  const canGoPrev = currentIndex > 0;

  return (
    <motion.div
      className="
        fixed inset-0 z-[60]
        flex flex-col
        bg-black/50
      "
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
        paddingRight: 'env(safe-area-inset-right)',
      }}
      variants={variants}
      initial="hidden"
      animate="visible"
      exit="exit"
      data-testid="mobile-fullscreen-sticky"
    >
      {/* Main card container */}
      <div
        className={`
          flex-1 m-3 flex flex-col
          ${colorScheme.bg} ${colorScheme.border}
          border-2 rounded-2xl shadow-2xl
          overflow-hidden
        `}
      >
        {/* Header */}
        <div
          className={`
            ${colorScheme.header}
            px-4 py-3 flex items-center justify-between
            border-b ${colorScheme.border}
          `}
        >
          {/* Left: Nav prev + title */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {hasMultiple && (
              <button
                onClick={onPrev}
                disabled={!canGoPrev}
                className={`
                  p-2 rounded-full
                  min-w-[44px] min-h-[44px]
                  flex items-center justify-center
                  transition-all duration-150
                  ${canGoPrev
                    ? isDynamicsTheme
                      ? 'text-dark-muted hover:bg-dark-surface/40'
                      : 'text-dark-muted hover:bg-black/10'
                    : isDynamicsTheme
                      ? 'text-dark-text cursor-not-allowed'
                      : 'text-dark-text cursor-not-allowed'
                  }
                `}
                aria-label="Previous note"
              >
                <ChevronLeft size={24} />
              </button>
            )}

            <StickyNote size={20} className={colorScheme.iconColor} aria-hidden="true" />

            {isEditingTitle ? (
              <input
                ref={titleInputRef}
                type="text"
                value={localTitle}
                onChange={handleTitleChange}
                onBlur={handleTitleBlur}
                onKeyDown={handleTitleKeyDown}
                className={`
                  flex-1 min-w-0 px-2 py-1 text-base font-medium rounded
                  ${isDynamicsTheme ? 'bg-dark-surface border-dark-border/30' : 'bg-dark-surface/50/10 border border-dark-border/30'}
                  ${colorScheme.headerText}
                  focus:outline-none focus:ring-2 focus:ring-brand-400
                `}
                aria-label="Edit sticky note title"
              />
            ) : (
              <button
                onClick={() => setIsEditingTitle(true)}
                className="flex items-center gap-2 flex-1 min-w-0 text-left"
              >
                <h2 className={`font-semibold text-lg truncate ${colorScheme.headerText}`}>
                  {localTitle}
                </h2>
                <Pencil size={14} className={`${colorScheme.iconColor} opacity-60 flex-shrink-0`} />
              </button>
            )}
          </div>

          {/* Right: Nav next + actions */}
          <div className="flex items-center gap-1 ml-2">
            {hasMultiple && (
              <button
                onClick={onNext}
                disabled={!canGoNext}
                className={`
                  p-2 rounded-full
                  min-w-[44px] min-h-[44px]
                  flex items-center justify-center
                  transition-all duration-150
                  ${canGoNext
                    ? isDynamicsTheme
                      ? 'text-dark-muted hover:bg-dark-surface/40'
                      : 'text-dark-muted hover:bg-black/10'
                    : isDynamicsTheme
                      ? 'text-dark-text cursor-not-allowed'
                      : 'text-dark-text cursor-not-allowed'
                  }
                `}
                aria-label="Next note"
              >
                <ChevronRight size={24} />
              </button>
            )}

            <button
              onClick={handleCopy}
              className={`
                p-2 rounded-full
                min-w-[44px] min-h-[44px]
                flex items-center justify-center
                transition-all duration-150
                ${isDynamicsTheme ? 'text-dark-muted hover:bg-dark-surface/40' : 'text-dark-muted hover:bg-black/10'}
              `}
              aria-label={hasCopied ? 'Copied!' : 'Copy note content'}
            >
              {hasCopied ? (
                <Check size={22} className="text-green-500" />
              ) : (
                <Copy size={22} />
              )}
            </button>

            <button
              onClick={onMinimize}
              className={`
                p-2 rounded-full
                min-w-[44px] min-h-[44px]
                flex items-center justify-center
                transition-all duration-150
                ${isDynamicsTheme ? 'text-dark-muted hover:bg-dark-surface/40' : 'text-dark-muted hover:bg-black/10'}
              `}
              aria-label="Minimize note"
            >
              <Minus size={22} />
            </button>

            <button
              onClick={onDelete}
              className={`
                p-2 rounded-full
                min-w-[44px] min-h-[44px]
                flex items-center justify-center
                transition-all duration-150
                ${isDynamicsTheme ? 'text-red-600 hover:bg-red-100' : 'text-red-500 hover:bg-red-900/30'}
              `}
              aria-label="Delete note"
            >
              <X size={22} />
            </button>
          </div>
        </div>

        {/* Pagination indicator */}
        {hasMultiple && (
          <div className="px-4 py-2 text-center">
            <span className={`text-sm ${isDynamicsTheme ? 'text-dark-muted' : 'text-dark-muted'}`}>
              {currentIndex + 1} of {totalCount}
            </span>
          </div>
        )}

        {/* Body - Full height textarea */}
        <div className="flex-1 px-4 pb-4 flex flex-col min-h-0">
          <textarea
            ref={textareaRef}
            value={localContent}
            onChange={handleContentChange}
            placeholder={DEFAULT_STICKY_PLACEHOLDER}
            className={`
              w-full flex-1 p-3 rounded-xl
              ${isDynamicsTheme ? 'bg-dark-surface/80' : 'bg-dark-surface/30'}
              text-base leading-relaxed
              ${isDynamicsTheme ? 'text-white placeholder-dark-muted' : 'text-white placeholder-dark-muted'}
              resize-none
              focus:outline-none focus:ring-2 focus:ring-brand-500/50
              overflow-y-auto
            `}
            aria-label={`${sticky.title} content`}
          />
        </div>
      </div>

      {/* Tap outside to minimize hint */}
      <div
        className="absolute inset-0 -z-10"
        onClick={onMinimize}
        aria-hidden="true"
      />
    </motion.div>
  );
};

